import React, { useState } from 'react';
import adminApi from '../api/client';

const AccountSecurity: React.FC = () => {
  const [codeRequested, setCodeRequested] = useState(false);
  const [requestPassword, setRequestPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const requestCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await adminApi.post('/admin/account/password-change/request-otp', {
        current_password: requestPassword,
      });
      setRequestPassword('');
      setCodeRequested(true);
      setMessage(response.data.message);
    } catch (requestError: any) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not send a verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await adminApi.post('/admin/account/password-change', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_new_password: confirmPassword,
        otp,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setOtp('');
      window.location.href = '/login';
    } catch (requestError: any) {
      const detail = requestError.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not change the password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setCodeRequested(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setMessage('');
    setError('');
  };

  return (
    <section className="max-w-3xl animate-fade-in">
      <div className="mb-7">
        <p className="text-sm font-semibold uppercase tracking-widest text-accent">Admin account</p>
        <h1 className="mt-2 text-3xl font-bold text-gray-900">Account security</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
          Change your own admin password. We verify your current password and send a one-time code to your verified admin email.
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-start gap-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <span className="text-xl" aria-hidden="true">🔐</span>
          <p className="text-sm leading-6 text-amber-900">
            Changing your password signs out all existing admin sessions. You will need to sign in again with your password and two-factor code.
          </p>
        </div>

        {message && (
          <p role="status" className="mb-5 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {message}
          </p>
        )}
        {error && (
          <p role="alert" className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </p>
        )}

        {!codeRequested ? (
          <form onSubmit={requestCode} className="space-y-5">
            <div>
              <label htmlFor="request-current-password" className="mb-2 block text-sm font-semibold text-gray-700">
                Current password
              </label>
              <input
                id="request-current-password"
                type="password"
                autoComplete="current-password"
                required
                value={requestPassword}
                onChange={(event) => setRequestPassword(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Sending code…' : 'Verify password and send code'}
            </button>
          </form>
        ) : (
          <form onSubmit={changePassword} className="space-y-5">
            <div>
              <label htmlFor="password-change-otp" className="mb-2 block text-sm font-semibold text-gray-700">
                Email verification code
              </label>
              <input
                id="password-change-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm tracking-[0.35em] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label htmlFor="confirm-current-password" className="mb-2 block text-sm font-semibold text-gray-700">
                Confirm current password
              </label>
              <input
                id="confirm-current-password"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label htmlFor="new-admin-password" className="mb-2 block text-sm font-semibold text-gray-700">
                New password
              </label>
              <input
                id="new-admin-password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <p className="mt-1.5 text-xs text-gray-500">At least 8 characters. Passwords are limited to bcrypt’s 72-byte input size.</p>
            </div>
            <div>
              <label htmlFor="confirm-admin-password" className="mb-2 block text-sm font-semibold text-gray-700">
                Confirm new password
              </label>
              <input
                id="confirm-admin-password"
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={loading}
                className="rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Changing password…' : 'Change password and sign out'}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={startOver}
                className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
              >
                Request a new code
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default AccountSecurity;
